/**
 * V2 fullscreen photo viewer — V1's modals/fullscreen-image.js ported
 * whole (M8.5 Tier 1): tap a photo to see it full-bleed, swipe or arrow
 * between photos, download it, or tap the image to hide every control
 * for a clean screenshot ("for viewing, downloading, or screenshotting"
 * — V1's own header). Native <dialog> gives Escape + focus restore.
 *
 * Keyboard: ← → navigate · Space hides/shows controls · Esc closes.
 * Touch: swipe left/right (50px threshold, 10px axis disambiguation,
 * V1 fullscreen-image.js:613-662). Backdrop tap closes.
 */
import { escapeHtml } from '../../assets/js/utils.js';

const SWIPE_THRESHOLD = 50; // px, V1 parity
const AXIS_SLOP = 10; // horizontal-vs-vertical disambiguation

let dialog = null;
let urls = [];
let index = 0;
let title = '';

function filenameFor(url) {
  try {
    const path = new URL(url, window.location.origin).pathname;
    return decodeURIComponent(path.split('/').pop() || 'photo.jpg');
  } catch {
    return 'photo.jpg';
  }
}

function render() {
  const many = urls.length > 1;
  dialog.innerHTML = `
    <div class="pv-stage" data-pv-stage>
      <img class="pv-image" src="${urls[index]}" alt="${escapeHtml(title)}" data-pv-image>
      ${many ? `
      <button type="button" class="pv-arrow pv-prev" data-pv-prev aria-label="Previous photo">‹</button>
      <button type="button" class="pv-arrow pv-next" data-pv-next aria-label="Next photo">›</button>` : ''}
      <div class="pv-chrome pv-top">
        ${many ? `<span class="pv-counter" aria-live="polite">${index + 1} / ${urls.length}</span>` : '<span></span>'}
        <span class="pv-actions">
          <button type="button" class="pv-btn" data-pv-download>Download</button>
          <button type="button" class="pv-btn" data-pv-close aria-label="Close">✕</button>
        </span>
      </div>
      <div class="pv-chrome pv-hint">Tap the photo to hide the controls</div>
    </div>`;
}

function show(delta) {
  if (!urls.length) return;
  index = (index + delta + urls.length) % urls.length; // wrap like V1
  render();
}

function toggleChrome() {
  dialog?.classList.toggle('pv-bare');
}

async function download() {
  const url = urls[index];
  try {
    // fetch → blob → anchor, V1 fullscreen-image.js:547-579 (a plain
    // href download is ignored cross-origin)
    const blob = await (await fetch(url)).blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filenameFor(url);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  } catch {
    window.open(url, '_blank', 'noopener'); // last resort: new tab
  }
}

function onClick(event) {
  if (event.target.closest('[data-pv-prev]')) return show(-1);
  if (event.target.closest('[data-pv-next]')) return show(1);
  if (event.target.closest('[data-pv-download]')) return download();
  if (event.target.closest('[data-pv-close]')) return close();
  if (event.target.closest('[data-pv-image]')) return toggleChrome();
  if (!event.target.closest('[data-pv-stage]')) return close(); // backdrop
}

function onKeydown(event) {
  if (event.key === 'ArrowLeft') show(-1);
  else if (event.key === 'ArrowRight') show(1);
  else if (event.key === ' ') {
    // A focused button keeps its native Space activation — the chrome
    // toggle only owns Space when nothing interactive holds focus
    if (event.target instanceof Element && event.target.closest('button')) return;
    event.preventDefault();
    toggleChrome();
  }
}

let touchX = null;
let touchY = null;

function onTouchStart(event) {
  touchX = event.touches[0]?.clientX ?? null;
  touchY = event.touches[0]?.clientY ?? null;
}

function onTouchEnd(event) {
  if (touchX == null) return;
  const dx = (event.changedTouches[0]?.clientX ?? touchX) - touchX;
  const dy = (event.changedTouches[0]?.clientY ?? touchY) - touchY;
  touchX = touchY = null;
  if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy) + AXIS_SLOP) return;
  show(dx < 0 ? 1 : -1);
}

function close() {
  dialog?.close();
}

function destroy() {
  if (!dialog) return;
  dialog.removeEventListener('click', onClick);
  dialog.removeEventListener('keydown', onKeydown);
  dialog.removeEventListener('touchstart', onTouchStart);
  dialog.removeEventListener('touchend', onTouchEnd);
  dialog.remove();
  dialog = null;
  urls = [];
}

/**
 * Open the viewer.
 * @param {string[]} photoUrls
 * @param {number} startIndex
 * @param {object} opts - { title } for the image alt text
 */
export function openPhotoViewer(photoUrls, startIndex = 0, { title: alt = '' } = {}) {
  if (!photoUrls?.length) return;
  destroy();
  urls = photoUrls;
  index = Math.min(Math.max(startIndex, 0), urls.length - 1);
  title = alt;
  dialog = document.createElement('dialog');
  dialog.className = 'photo-viewer';
  dialog.setAttribute('aria-label', alt ? `Photos: ${alt}` : 'Photos');
  document.body.appendChild(dialog);
  dialog.addEventListener('click', onClick);
  dialog.addEventListener('keydown', onKeydown);
  dialog.addEventListener('touchstart', onTouchStart, { passive: true });
  dialog.addEventListener('touchend', onTouchEnd, { passive: true });
  dialog.addEventListener('close', destroy, { once: true });
  render();
  dialog.showModal?.();
}
